import { useCallback, useEffect, useState } from 'react'

const IS_SERVER = typeof window === 'undefined'

/**
 * Custom hook for using local storage to persist state across page reloads.
 * @param {string} key - The key under which the value will be stored in local storage.
 * @param {*} initialValue - The initial value of the state or a function that returns the initial value.
 * @returns {Array} A tuple containing the stored value and a function to set the value.
 */
export default function useLocalStorage(key, initialValue) {
  const readValue = useCallback(() => {
    const initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue

    if (IS_SERVER) {
      return initialValueToUse
    }

    try {
      const raw = window.localStorage.getItem(key)
      return raw ? JSON.parse(raw) : initialValueToUse
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValueToUse
    }
  }, [initialValue, key])

  const [storedValue, setStoredValue] = useState(() => readValue())

  const setValue = useCallback(
    value => {
      if (IS_SERVER) {
        console.warn(`Tried setting localStorage key "${key}" even though environment is not a client`)
        return
      }

      try {
        const newValue = value instanceof Function ? value(readValue()) : value
        window.localStorage.setItem(key, JSON.stringify(newValue))
        setStoredValue(newValue)

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('local-storage', { key }))
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, readValue],
  )

  useEffect(() => {
    const handleStorageChange = event => {
      if ((event?.key && event.key !== key) || !event?.key) {
        return
      }

      const newValue = readValue()
      const areEqual =
        typeof newValue === 'object' && newValue !== null
          ? JSON.stringify(newValue) === JSON.stringify(storedValue)
          : newValue === storedValue

      if (!areEqual) {
        setStoredValue(newValue)
      }
    }

    if (!IS_SERVER) {
      window.addEventListener('storage', handleStorageChange)
      window.addEventListener('local-storage', handleStorageChange)

      return () => {
        window.removeEventListener('storage', handleStorageChange)
        window.removeEventListener('local-storage', handleStorageChange)
      }
    }

    return undefined
  }, [key, readValue, storedValue])

  return [storedValue, setValue]
}
